import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { CardsSkeleton } from '@/components/ui/skeleton';
import { CheckBlock, ProfileForm, ProfileMissingBlock } from './PrimaNotaCheck';
import { DeclarationsBlock } from './PrimaNotaDeclarations';
import { ProducedFindings } from './PrimaNotaFindings';
import { GeneratePanel } from './PrimaNotaGenerate';
import { PrimaNotaHeader } from './PrimaNotaHeader';
import { ProducedSummary } from './PrimaNotaProduced';
import { SupplyBlock } from './PrimaNotaSupply';
import { buildArchiveRows, buildStatementSlots } from './prima-nota-archive';
import { deriveReadiness } from './prima-nota-derived';
import { base64ToBlob, type ProducedRegister, type Readiness } from './prima-nota-model';
import { monthLabel } from './prima-nota-period';
import { usePrimaNotaPage, type PrimaNotaPage } from './usePrimaNotaPage';

const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

const download = (register: ProducedRegister) => {
  const url = URL.createObjectURL(base64ToBlob(register.workbookBase64, XLSX_MIME));
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = register.filename;
  anchor.click();
  URL.revokeObjectURL(url);
};

function ProfileFormFor({ page }: { page: PrimaNotaPage }) {
  return (
    <ProfileForm
      profileNote={page.judgement.profileNote}
      onNoteChange={page.judgement.setProfileNote}
      busy={page.slots.busy === 'profile'}
      locked={page.locked}
      onPick={(file) => void page.actions.uploadProfile(file)}
    />
  );
}

function CheckAndSupply({ page, data }: { page: PrimaNotaPage; data: Readiness }) {
  const { t } = useTranslation('finance');
  const { slots, judgement, actions } = page;
  const derived = deriveReadiness(data);
  const verdictText = derived.fromNexpense
    ? [
        t('primaNota.check.fromNexpense'),
        ...(derived.findings.length > 0 ? [t('primaNota.check.alsoWarnings')] : []),
      ].join(' ')
    : t(`primaNota.check.${derived.verdict}`);

  return (
    <div className="space-y-6">
      <CheckBlock
        data={data}
        verdict={derived.verdict}
        verdictText={verdictText}
        locked={page.locked}
        profileBusy={slots.busy === 'profile'}
        revisingProfile={judgement.revisingProfile}
        onRevise={() => judgement.setRevisingProfile(true)}
        onCancelRevise={() => judgement.setRevisingProfile(false)}
        profileForm={<ProfileFormFor page={page} />}
      />
      <SupplyBlock
        data={data}
        documentsSupplied={derived.documentsSupplied}
        archiveRows={buildArchiveRows(data.statements)}
        statementSlots={buildStatementSlots(data.statements)}
        dateFormat={page.dateFormat}
        booksDay={page.booksDay}
        locked={page.locked}
        busy={slots.busy}
        withdrawing={slots.withdrawing}
        withdrawReason={slots.withdrawReason}
        onUpload={(file, kind, accountKey, format) =>
          void actions.upload(file, kind, accountKey, format)
        }
        onFetchDocuments={() => void actions.fetchDocuments()}
        onSweep={() => void actions.sweepArchive()}
        onStartWithdraw={(sourceId) => {
          slots.setWithdrawing(sourceId);
          slots.setWithdrawReason('');
        }}
        onReasonChange={slots.setWithdrawReason}
        onWithdraw={(sourceId) => void actions.withdraw(sourceId)}
        onCancelWithdraw={() => slots.setWithdrawing(null)}
      />
    </div>
  );
}

function WorkPanel({ page, data }: { page: PrimaNotaPage; data: Readiness }) {
  const { slots, judgement, actions } = page;
  const { produced } = slots;
  const derived = deriveReadiness(data);

  return (
    <div className="work-panel space-y-4">
      {produced !== null && (
        <ProducedSummary
          register={produced}
          onDownload={() => download(produced)}
          // The findings sit at the foot of the main column, far below on a phone, so the
          // panel's «vedi l'esito» takes the focus there rather than leaving the reader to scroll.
          onShowFindings={() => document.getElementById('prima-nota-findings')?.focus()}
        />
      )}
      <GeneratePanel
        monthName={monthLabel(page.period, page.language)}
        produced={produced}
        verdict={derived.verdict}
        generable={derived.generable}
        canGenerate={data.canGenerate}
        reviewed={page.reviewed}
        onReviewedChange={page.setReviewed}
        notes={judgement.notes}
        onNotesChange={judgement.setNotes}
        locked={page.locked}
        generating={slots.busy === 'generate'}
        message={slots.message}
        onGenerate={() => void actions.generate()}
      />
    </div>
  );
}

function JudgementColumn({ page }: { page: PrimaNotaPage }) {
  const { slots, judgement, actions } = page;
  return (
    <div className="space-y-6">
      <DeclarationsBlock
        declarations={judgement.declarations}
        onAdd={judgement.addDeclaration}
        onEdit={judgement.editDeclaration}
        onRemove={judgement.removeDeclaration}
      />
      {slots.produced !== null && (
        <ProducedFindings
          register={slots.produced}
          treatments={judgement.treatments}
          busy={slots.busy === 'generate'}
          locked={page.locked}
          onTreatmentChange={judgement.setTreatment}
          onRegenerate={() => void actions.generate()}
        />
      )}
    </div>
  );
}

/**
 * The three operator surfaces, on one screen, as three numbered steps.
 *
 * SL-0058 tasks 7.1, 7.2 and 7.3 name three surfaces. They are three STEPS of one screen rather
 * than three routes, and that is a deliberate reading of the Human's own instruction that this had
 * to become usable in practice by someone who is not technical. The three things are strictly
 * sequential — see what is missing, supply it, produce the register — and splitting a sequence
 * across three URLs makes the user hold the state in their head and navigate between pages to
 * answer "can I go yet". One screen answers it by being looked at.
 *
 * Nothing is hidden by that choice: each step is separately addressable content with its own
 * heading, and the underlying routes remain three independent calls.
 */
export function FinancePrimaNotaPage() {
  const { t } = useTranslation('finance');
  const page = usePrimaNotaPage();
  const { view } = page;

  if (view.status === 'loading') {
    return <CardsSkeleton />;
  }

  if (view.status === 'error') {
    return (
      <div className="p-6">
        <p className="text-sm">{t('primaNota.loadError')}</p>
        <Button className="mt-3" onClick={view.reload}>
          {t('primaNota.retry')}
        </Button>
      </div>
    );
  }

  const data: Readiness = view.data;

  return (
    <div className="space-y-6">
      <PrimaNotaHeader
        period={page.period}
        actionRunning={page.slots.busy !== null}
        onChange={page.changePeriod}
      />

      {data.profileRevision === null && (
        <ProfileMissingBlock profileForm={<ProfileFormFor page={page} />} />
      )}

      <div className="work-with-panel">
        <CheckAndSupply page={page} data={data} />
        <WorkPanel page={page} data={data} />
        {/* After the panel in the document, so the thing you came to do comes before the thing you
            usually skip (FinancePrimaNotaPage.test.tsx, "puts the produce button above the
            declared-costs editor"). Beside the panel's column it still reads as the left column. */}
        <JudgementColumn page={page} />
      </div>
    </div>
  );
}
